import { ListIllustration } from "vega-illustrations";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { EmptyCard } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { m } from "@vega-core/paraglide/messages";

import { useCoInvestmentData } from "../../hooks/useCoInvestmentData";
import { CoInvestmentItem } from "../CoInvestmentItem/CoInvestmentItem";
import { CoInvestmentItemSkeleton } from "../CoInvestmentItem/CoInvestmentItemSkeleton";

export type CoInvestmentsListProps = {
  onCreateClick: () => void;
  canCreateDeal?: boolean;
};

export const CoInvestmentsList = ({
  onCreateClick,
  canCreateDeal,
}: CoInvestmentsListProps) => {
  const { deals, isLoading, paginationElement, RenderFilters } =
    useCoInvestmentData();

  const hasDeals = deals.length > 0;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {RenderFilters}
        <div className="flex flex-col divide-y-1 divide-border/primary rounded-default">
          {Array.from({ length: 3 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <CoInvestmentItemSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (!hasDeals) {
    return (
      <div className="flex flex-col gap-4">
        {RenderFilters}
        <EmptyCard
          actions={
            canCreateDeal ? (
              <ThemedButton
                dataTestId="create-co-investment-empty-button"
                onClick={onCreateClick}
                variant="secondary"
              >
                {m.coInvestments_list_gp_createNew()}
              </ThemedButton>
            ) : undefined
          }
          description={canCreateDeal ? m.common_getStarted() : undefined}
          illustration={<ListIllustration />}
          title={
            canCreateDeal
              ? m.coInvestments_list_gp_createFirstTitle()
              : m.coInvestments_list_gp_notFound()
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {RenderFilters}

      <div className="flex flex-col divide-y-1 divide-border/primary rounded-default">
        {deals.map((deal) => (
          <CoInvestmentItem item={deal} key={deal.id} />
        ))}
      </div>

      <div className="px-3 md:px-8">{paginationElement}</div>
    </div>
  );
};
